import React, { useState } from 'react';
import { Badge, Button, Card, Icon, Notice, Text } from '@heartinternet/hi-ui';
import type { SSLInfoContext } from '../SSLInfoPage';
import type { OvEvValidations, SSLCheckData, ValidationStatus } from '../types';
import {
  endpoints,
  getValidationStatusIcon,
  getValidationStatusText,
  getValidationStepModifier,
  OV_EV_SECTION_LABELS,
  rpcGet,
} from '../helpers';

export interface ValidationProps {
  validations: OvEvValidations;
  ctx: SSLInfoContext;
}

interface Message {
  text: string;
  isError: boolean;
}

/** Same status across all sections? — mirrors isSameValidationStatus. */
function sameValidations(a: OvEvValidations, b: OvEvValidations): boolean {
  return (Object.keys(a) as (keyof OvEvValidations)[]).every((k) => a[k] === b[k]);
}

/** Individual/Organisation Validation card — mirrors submitted/pending-validation.js. */
export const Validation: React.FC<ValidationProps> = ({ validations, ctx }) => {
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);

  const rows = (Object.entries(validations) as [keyof OvEvValidations, ValidationStatus | null][])
    .filter(([, status]) => Boolean(status))
    .map(([key, status]) => ({
      key,
      label: OV_EV_SECTION_LABELS[key],
      status: status as ValidationStatus,
    }));

  const handleCheck = async () => {
    setMessage(null);
    setChecking(true);
    const res = await rpcGet<SSLCheckData>(ctx.fetchFn, endpoints.check(ctx.baseUrl, ctx.oid));
    setChecking(false);
    if (res.error || !res.data) {
      setMessage({ text: res.error ?? 'We could not check the validation status.', isError: true });
      return;
    }
    if (sameValidations(res.data.ov_ev_validations, validations)) {
      setMessage({ text: 'Validation status is the same as before', isError: false });
      return;
    }
    ctx.refetch();
  };

  return (
    <Card
      variant="surface"
      header={
        <div className="hi-ui-ssl-info__section-header">
          <Text variant="heading-3">Individual/organisation validation</Text>
          <Badge variant="warning">Pending</Badge>
        </div>
      }
    >
      <div className="hi-ui-ssl-info__section-body">
        <Text variant="body">Complete these validation steps.</Text>
        <ul className="hi-ui-ssl-info__validation-steps">
          {rows.map((row) => (
            <li
              key={row.key}
              className={`hi-ui-ssl-info__validation-step hi-ui-ssl-info__validation-step--${getValidationStepModifier(
                row.status
              )}`}
            >
              <Icon name={getValidationStatusIcon(row.status)} />
              <div className="hi-ui-ssl-info__validation-content">
                <Text variant="inline" bold>
                  {row.label}
                </Text>
                <Text variant="inline" className="hi-ui-ssl-info__validation-status">
                  {getValidationStatusText(row.status)}
                </Text>
              </div>
            </li>
          ))}
        </ul>
        {message && <Notice variant={message.isError ? 'error' : 'success'}>{message.text}</Notice>}
        <div className="hi-ui-ssl-info__actions">
          <Button variant="primary" loading={checking} onClick={handleCheck}>
            Check status
          </Button>
        </div>
      </div>
    </Card>
  );
};
